// import { useSetAtom } from "jotai";
import React, {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// import { errorMessageAtom, isInProgressAtom } from "../atoms/messageBarAtoms";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  FluentProvider,
  makeStyles,
  shorthands,
  Spinner,
  Switch,
  tokens,
  Toolbar,
  ToolbarButton,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { useActiveTheme } from './theme/ThemeContext';
import {
  validate,
  parseOtherInfo,
  type IMetadata,
  type IValidationError,
} from '@moko/utils';
import OtherInfo from './OtherInfo';
import type { GetTenantsFn } from './TenantSelector/types';
import {
  Checkmark24Regular,
  DismissCircleRegular,
  EditRegular,
  SaveFilled,
} from '@fluentui/react-icons';

export type SaveItemFn = (opts?: { skipIfClean?: boolean }) => Promise<boolean>;

export interface IEditItemToolbarButton {
  text: string;
  onClick: () => void;
  icon?: React.JSX.Element;
  iconPosition?: 'before' | 'after';
  disabled?: boolean;
}

export interface IEditItemParams {
  metadata: IMetadata;
  isSaved: React.MutableRefObject<boolean>;
  defaultValues?: string;
  getAction: (abortController: AbortController, id: number) => Promise<unknown>;
  newAction: (
    abortController: AbortController,
    item: Record<string, unknown>
  ) => void | Promise<void>;
  editAction: (
    abortController: AbortController,
    id: number,
    item: Record<string, unknown>
  ) => void | Promise<void>;
  afterChange?: (item: Record<string, unknown>) => void;
  preButtons?: IEditItemToolbarButton[];
  postButtons?: IEditItemToolbarButton[];
  itemIdOverride?: number;
  onCancel?: () => void;
  back?: string;
  callGetBeforeNew?: boolean;

  /**
   * Optional external dirty flag for changes managed outside EditItem's internal form state.
   * Example: related entity selections stored in the parent component.
   */
  isDirtyExternal?: boolean;

  /** When true (default), Cancel prompts if there are unsaved changes. */
  confirmOnCancel?: boolean;

  /** Custom confirmation message shown when Cancel is pressed with unsaved changes. */
  confirmCancelMessage?: string;
  relatedEntities?:
    | {
        id: number;
        entityInfo: string;
      }[]
    | undefined;
  relatedEntityItems?: Record<string, unknown>[];
  entityMetadataMap?: Record<string, IMetadata>;
  onCreateAsset?: (values: {
    code: string;
    name: string;
    statusId?: number;
    metadataName?: string;
    parentId?: number;
    parentName?: string;
    otherInfo?: Record<string, unknown>;
  }) => Promise<{ id: number; code: string; name: string }>;
  onAddAsset?: () => void;
  onEditAsset?: (entityId: number) => void;
  onRemoveAsset?: (entityId: number) => void;
  contractorCreatedAssetIds?: Set<number>;
  assetStatuses?: { id: number; name: string }[];
  assetTemplates?: { key: string; value: string }[];
  parentAssets?: { id: number; code: string; name: string }[];
  onTemplateChange?: (
    metadataName: string | undefined,
    abortController: AbortController
  ) => Promise<IMetadata | undefined>;
  onFetchAutoComplete?: (
    abortController: AbortController,
    refName: string,
    searchText: string,
    dependees?: string
  ) => Promise<{ key: string; value: string }[]>;
  renderAfterField?: (fieldName: string) => React.ReactNode;
  onFetchUsers?: (
    abortController: AbortController,
    pageSize: number,
    pageNo: number,
    orderBy: string | undefined,
    search: string | undefined
  ) => Promise<{
    items: {
      id: number;
      username: string;
      firstName?: string;
      lastName?: string;
      email?: string;
    }[];
  }>;
  hideCode?: boolean;
  onFetchTenants?: GetTenantsFn;
  otherInfoMode?: 'desktop' | 'mobile' | 'desktop-split';
  /**
   * Initial read-only state for the OtherInfo fields/actions. When
   * {@link otherInfoCanToggleEdit} is also true, this is just the default and
   * the user can switch to edit mode via the toolbar "Edit" button.
   */
  otherInfoReadOnly?: boolean;
  /**
   * When true, the toolbar shows an "Edit" button (in place of Save) while
   * read-only, letting the user toggle between view and edit. Used for the
   * manager role in the client app. Clients pass false so they stay view-only.
   */
  otherInfoCanToggleEdit?: boolean;
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;
  hideToolbar?: boolean;
  actionsPlacement?: 'toolbar' | 'bottom';
  saveDisabled?: boolean;
  /**
   * When true, OtherInfo section/tab navigation persists the current form
   * via {@link editAction} / {@link newAction} before switching.
   */
  saveOnTabChange?: boolean;
  onFormFinish?: () => void | Promise<void>;
  finishInsteadOfSave?: boolean;
  saveRef?: React.MutableRefObject<SaveItemFn | null>;
  revertRef?: React.MutableRefObject<(() => void) | null>;
  cancelRef?: React.MutableRefObject<(() => void) | null>;
  patchValuesRef?: React.MutableRefObject<
    ((patch: Record<string, unknown>) => void) | null
  >;
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * Optional content rendered at the top of the form body (after the toolbar,
   * before the fields) inside the same column context as the fields, so a
   * horizontal `Field` here lines up with the form rows below.
   */
  headerSlot?: React.ReactNode;
  /**
   * Keep the toolbar on screen while the form body scrolls. Other screens
   * leave this off.
   */
  pinToolbar?: boolean;
  pinnedHeader?: React.ReactNode;
}

const editItemStyles = makeStyles({
  root: {
    display: 'grid',
    gridTemplateRows: 'repeat(1fr)',
    justifyItems: 'start',
    ...shorthands.gap('20px'),
    ...shorthands.padding('5px', '0px', '10px'),
  },
  spinnerContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100px',
  },
  iconCancel: {
    color: tokens.colorPaletteRedForeground2,
    fontSize: '64px',
  },
  iconSave: {
    color: tokens.colorPaletteLightGreenForeground2,
    fontSize: '64px',
  },
  iconEdit: {
    color: tokens.colorBrandForeground1,
    fontSize: '64px',
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    justifySelf: 'stretch',
    justifyContent: 'flex-end',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalM),
    width: '100%',
  },
  pinRoot: {
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
  },
  pinFrame: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    width: '100%',
    overflow: 'hidden',
  },
  pinToolbar: {
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    ...shorthands.gap('8px'),
    ...shorthands.padding('0px', '0px', '8px'),
  },
  pinBody: {
    flexGrow: 1,
    minHeight: 0,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    ...shorthands.gap('20px'),
  },
  toolbarEnd: {
    justifySelf: 'stretch',
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
});

function FormFrame({
  pin,
  frameClassName,
  bodyClassName,
  children,
}: {
  pin: boolean;
  frameClassName: string;
  bodyClassName: string;
  children: React.ReactNode;
}) {
  if (!pin) return <>{children}</>;
  const [toolbar, ...body] = React.Children.toArray(children);
  return (
    <div className={frameClassName}>
      {toolbar}
      <div className={bodyClassName}>{body}</div>
    </div>
  );
}

const EditItem = (params: PropsWithChildren<IEditItemParams>) => {
  const { t } = useTranslation();
  const theme = useActiveTheme();
  const children = React.Children.toArray(params.children);
  const query = new URLSearchParams(useLocation().search);
  const routeItemId = Number.parseInt(query.get('id') ?? '');
  const itemId =
    params.itemIdOverride !== undefined ? params.itemIdOverride : routeItemId;

  const navigate = useNavigate();

  const actionsAtBottom = params.actionsPlacement === 'bottom';

  // Values that are not fetched are known before the first render, so seed
  // them here. Applying them in the mount effect instead would render the
  // fields once as empty, and that empty render is reported to the consumer as
  // a change.
  const willFetch = itemId > 0 || Boolean(params.callGetBeforeNew);
  const seededValues = willFetch ? '{}' : params.defaultValues ?? '{}';
  const [otherInfo, setOtherInfo] = useState(seededValues);
  const [initialOtherInfo, setInitialOtherInfo] =
    useState<string>(seededValues);
  const [validationErrors, setValidationErrors] = useState<IValidationError[]>(
    []
  );
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  // const setErrorMessage = useSetAtom(errorMessageAtom);
  // const setIsInProgress = useSetAtom(isInProgressAtom);
  const [isLoading, setIsLoading] = useState(false);
  const saveInFlightRef = useRef<Promise<boolean> | null>(null);
  const finishInFlightRef = useRef(false);
  const handleSaveRef = useRef<SaveItemFn | null>(null);

  // Edit-mode toggle (manager in the client app). When the consumer allows
  // toggling, the fields start read-only and the manager opts into editing.
  const [internalEditMode, setInternalEditMode] = useState(false);
  const isControlledEditing =
    params.editing !== undefined && params.onEditingChange != null;
  const editMode = isControlledEditing
    ? Boolean(params.editing)
    : internalEditMode;
  const setEditMode = (next: boolean) => {
    if (isControlledEditing) {
      params.onEditingChange?.(next);
      return;
    }
    setInternalEditMode(next);
  };
  const [unansweredOnly, setUnansweredOnly] = useState(false);
  const effectiveReadOnly = Boolean(params.otherInfoReadOnly) && !editMode;
  const showEditInTopToolbar =
    !isControlledEditing &&
    actionsAtBottom &&
    Boolean(params.otherInfoCanToggleEdit) &&
    effectiveReadOnly;
  const showUnansweredOnly =
    params.otherInfoMode === 'desktop-split' ||
    params.otherInfoMode === 'mobile';
  const isViewOnly = effectiveReadOnly && !params.otherInfoCanToggleEdit;
  const hasToolbarButtons =
    showEditInTopToolbar ||
    !actionsAtBottom ||
    Boolean(params.preButtons?.length || params.postButtons?.length);
  const showToolbar =
    !params.hideToolbar && (hasToolbarButtons || showUnansweredOnly);

  const fetchData = async () => {
    const abortController = new AbortController();
    // setIsInProgress(true);
    try {
      const data = await params.getAction(abortController, itemId);
      if (data == null) return;
      const parsed = parseOtherInfo(data);
      const next = JSON.stringify(parsed);
      setOtherInfo(next);
      setInitialOtherInfo(next);
    } catch (error: unknown) {
      console.error('Error:', error);
    } finally {
      // setIsInProgress(false);
      setIsLoading(false);
    }
    return () => {
      abortController.abort();
    };
  };

  useEffect(() => {
    if (!willFetch) return;
    // `fetchData` is async and clears `isLoading` in its own `finally` once
    // the request settles. Keep the flag on until then so the form shows a
    // spinner during the fetch instead of flashing empty field values.
    setIsLoading(true);
    fetchData();
  }, []);

  const styles = editItemStyles();

  const hasUnsavedChanges =
    otherInfo !== initialOtherInfo || Boolean(params.isDirtyExternal);

  React.useEffect(() => {
    params.onDirtyChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges]);

  const derivedValidationErrors = useMemo(() => {
    try {
      return validate(
        JSON.parse(otherInfo || '{}') as Record<string, unknown>,
        params.metadata.validations ?? [],
        params.metadata.fields
      );
    } catch {
      return [] as IValidationError[];
    }
  }, [otherInfo, params.metadata.validations, params.metadata.fields]);

  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!submitted) return;
    setValidationErrors(derivedValidationErrors);
  }, [submitted, derivedValidationErrors]);

  const hasValidationErrors =
    derivedValidationErrors.length > 0 || Boolean(params.saveDisabled);

  const handleSave = async (opts?: {
    skipIfClean?: boolean;
  }): Promise<boolean> => {
    if (effectiveReadOnly) return true;
    // Next/Previous use skipIfClean so a clean section does not PUT.
    // The Save button calls handleSave() without this flag.
    if (opts?.skipIfClean && !hasUnsavedChanges) return true;
    if (saveInFlightRef.current) return saveInFlightRef.current;

    const parsed = parseOtherInfo(otherInfo);
    if (derivedValidationErrors.length > 0) {
      setSubmitted(true);
      setValidationErrors(derivedValidationErrors);
      return false;
    }
    if (params.saveDisabled) return false;

    const abortController = new AbortController();
    const run = (async () => {
      try {
        if (!itemId) {
          await params.newAction(abortController, parsed);
        } else {
          await params.editAction(abortController, itemId, parsed);
        }
        const canonical = JSON.stringify(parsed);
        setOtherInfo(canonical);
        setInitialOtherInfo(canonical);
        return true;
      } catch {
        return false;
      }
    })();

    saveInFlightRef.current = run;
    try {
      return await run;
    } finally {
      if (saveInFlightRef.current === run) {
        saveInFlightRef.current = null;
      }
    }
  };

  const persistForm = useCallback((opts?: { skipIfClean?: boolean }) => {
    return handleSaveRef.current?.(opts) ?? Promise.resolve(true);
  }, []);

  const handleFinish = async () => {
    if (finishInFlightRef.current) return;
    finishInFlightRef.current = true;
    try {
      const saved = await handleSave();
      if (saved === false) return;
      await params.onFormFinish?.();
    } finally {
      finishInFlightRef.current = false;
    }
  };

  const exitEditMode = () => {
    // Revert any unsaved edits and return to the read-only view.
    setOtherInfo(initialOtherInfo);
    setValidationErrors([]);
    setEditMode(false);
  };

  const handleCancelButtonClick = () => {
    const confirmOnCancel = params.confirmOnCancel ?? true;
    if (confirmOnCancel && hasUnsavedChanges) {
      setIsCancelConfirmOpen(true);
      return;
    }
    // For toggle-capable consumers (manager), Cancel returns to read-only
    // rather than navigating away from the Work.
    if (params.otherInfoCanToggleEdit) {
      exitEditMode();
      return;
    }
    if (params.onCancel) {
      params.onCancel();
      return;
    }
    navigate(params.back ?? '');
  };

  React.useEffect(() => {
    handleSaveRef.current = handleSave;
    if (params.saveRef) params.saveRef.current = handleSave;
    if (params.revertRef) {
      params.revertRef.current = () => {
        setOtherInfo(initialOtherInfo);
        setValidationErrors([]);
      };
    }
    if (params.cancelRef) params.cancelRef.current = handleCancelButtonClick;
    if (params.patchValuesRef) {
      params.patchValuesRef.current = (patch: Record<string, unknown>) => {
        setOtherInfo((prev) => {
          const parsed = prev
            ? (JSON.parse(prev) as Record<string, unknown>)
            : {};
          const next = { ...parsed, ...patch };
          return JSON.stringify(next);
        });
      };
    }
  });

  return (
    <div
      className={
        params.pinToolbar ? `${styles.root} ${styles.pinRoot}` : styles.root
      }
    >
      {isLoading ? (
        <div className={styles.spinnerContainer}>
          <Spinner label="Loading..." /> {/* Fluent UI Spinner */}
        </div>
      ) : (
        <FormFrame
          pin={!!params.pinToolbar}
          frameClassName={styles.pinFrame}
          bodyClassName={styles.pinBody}
        >
          {showToolbar && (
            <div
              className={
                params.pinToolbar
                  ? styles.pinToolbar
                  : actionsAtBottom
                  ? styles.toolbarEnd
                  : undefined
              }
            >
              {params.pinnedHeader}
              {showUnansweredOnly && (
                <Switch
                  label={t('unansweredOnly', 'Unanswered Only')}
                  checked={unansweredOnly}
                  onChange={(_, data) => setUnansweredOnly(data.checked)}
                />
              )}
              {hasToolbarButtons && (
                <Toolbar aria-label="Default">
                  {params.preButtons?.map((button, index) => (
                    <ToolbarButton
                      key={button.text || index}
                      onClick={button.onClick}
                      icon={button.icon}
                      disabled={button.disabled}
                    >
                      {button.text}
                    </ToolbarButton>
                  ))}
                  {showEditInTopToolbar && (
                    <ToolbarButton
                      aria-label="Edit"
                      appearance="subtle"
                      icon={<EditRegular />}
                      onClick={() => setEditMode(true)}
                    >
                      Edit
                    </ToolbarButton>
                  )}
                  {!actionsAtBottom &&
                    (params.otherInfoCanToggleEdit &&
                    effectiveReadOnly &&
                    !isControlledEditing ? (
                      <ToolbarButton
                        aria-label="Edit"
                        appearance="subtle"
                        icon={<EditRegular className={styles.iconEdit} />}
                        onClick={() => setEditMode(true)}
                      >
                        Edit
                      </ToolbarButton>
                    ) : (
                      <>
                        {params.finishInsteadOfSave ? (
                          <ToolbarButton
                            aria-label="Finish"
                            appearance="subtle"
                            icon={<Checkmark24Regular />}
                            onClick={() => {
                              void handleFinish();
                            }}
                            disabled={hasValidationErrors}
                          >
                            {`${t('finish', 'Finish')}${
                              hasUnsavedChanges ? ' *' : ''
                            }`}
                          </ToolbarButton>
                        ) : (
                          <ToolbarButton
                            aria-label="Save"
                            appearance="subtle"
                            icon={<SaveFilled className={styles.iconSave} />}
                            onClick={() => {
                              void handleSave();
                            }}
                            disabled={hasValidationErrors}
                          >{`Save${
                            hasUnsavedChanges ? ' *' : ''
                          }`}</ToolbarButton>
                        )}
                        <ToolbarButton
                          aria-label="Cancel"
                          appearance="subtle"
                          icon={
                            <DismissCircleRegular
                              className={styles.iconCancel}
                            />
                          }
                          onClick={handleCancelButtonClick}
                        >
                          Cancel
                        </ToolbarButton>
                      </>
                    ))}
                  {params.postButtons?.map((button, index) => (
                    <ToolbarButton
                      key={button.text || index}
                      onClick={button.onClick}
                      icon={button.icon}
                      disabled={button.disabled}
                    >
                      {button.text}
                    </ToolbarButton>
                  ))}
                </Toolbar>
              )}
            </div>
          )}
          {params.headerSlot && (
            <div style={{ width: '100%' }}>{params.headerSlot}</div>
          )}
          <OtherInfo
            metadata={params.metadata}
            otherInfo={otherInfo}
            hideTitle={true}
            isSaved={params.isSaved}
            mode={params.otherInfoMode}
            readOnly={effectiveReadOnly}
            validationErrors={validationErrors}
            onValidationErrorsChanged={setValidationErrors}
            persistForm={params.saveOnTabChange ? persistForm : undefined}
            onFormFinish={params.onFormFinish}
            onOtherInfoChanged={(newValue: Record<string, unknown>) => {
              setOtherInfo(JSON.stringify(newValue));
              if (params.afterChange) {
                params.afterChange(newValue);
              }
            }}
            relatedEntities={params.relatedEntities}
            relatedEntityItems={params.relatedEntityItems}
            entityMetadataMap={params.entityMetadataMap}
            onCreateAsset={params.onCreateAsset}
            onAddAsset={params.onAddAsset}
            onEditAsset={params.onEditAsset}
            onRemoveAsset={params.onRemoveAsset}
            contractorCreatedAssetIds={params.contractorCreatedAssetIds}
            assetStatuses={params.assetStatuses}
            assetTemplates={params.assetTemplates}
            parentAssets={params.parentAssets}
            onTemplateChange={params.onTemplateChange}
            onFetchAutoComplete={params.onFetchAutoComplete}
            onFetchUsers={params.onFetchUsers}
            renderAfterField={params.renderAfterField}
            onFetchTenants={params.onFetchTenants}
            hideCode={params.hideCode}
            unansweredOnly={unansweredOnly}
          />
          {children}
          {!params.hideToolbar &&
            actionsAtBottom &&
            !showEditInTopToolbar &&
            !isViewOnly &&
            !effectiveReadOnly && (
              <div className={styles.actions}>
                <Button
                  aria-label="Cancel"
                  appearance="secondary"
                  icon={<DismissCircleRegular />}
                  onClick={handleCancelButtonClick}
                >
                  Cancel
                </Button>
                {params.finishInsteadOfSave ? (
                  <Button
                    aria-label="Finish"
                    appearance="primary"
                    icon={<Checkmark24Regular />}
                    iconPosition="after"
                    onClick={() => {
                      void handleFinish();
                    }}
                    disabled={hasValidationErrors}
                  >
                    {`${t('finish', 'Finish')}${hasUnsavedChanges ? ' *' : ''}`}
                  </Button>
                ) : (
                  <Button
                    aria-label="Save"
                    appearance="primary"
                    icon={<SaveFilled />}
                    onClick={() => {
                      void handleSave();
                    }}
                    disabled={hasValidationErrors}
                  >{`Save${hasUnsavedChanges ? ' *' : ''}`}</Button>
                )}
              </div>
            )}

          <FluentProvider theme={theme}>
            <Dialog
              open={isCancelConfirmOpen}
              onOpenChange={(_, data) => setIsCancelConfirmOpen(data.open)}
            >
              <DialogSurface>
                <DialogBody>
                  <DialogTitle>Discard changes?</DialogTitle>
                  <DialogContent>
                    {params.confirmCancelMessage ??
                      'All your unsaved changes will be lost.'}
                  </DialogContent>
                  <DialogActions>
                    <DialogTrigger disableButtonEnhancement>
                      <Button appearance="secondary">No</Button>
                    </DialogTrigger>
                    <Button
                      appearance="primary"
                      onClick={() => {
                        setIsCancelConfirmOpen(false);
                        if (params.otherInfoCanToggleEdit) {
                          exitEditMode();
                          return;
                        }
                        if (params.onCancel) {
                          params.onCancel();
                          return;
                        }
                        navigate(params.back ?? '');
                      }}
                    >
                      Yes
                    </Button>
                  </DialogActions>
                </DialogBody>
              </DialogSurface>
            </Dialog>
          </FluentProvider>
        </FormFrame>
      )}
    </div>
  );
};

export default EditItem;
